import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NumberFormatValues } from 'react-number-format';
import Link from 'redux-first-router-link';
import styled from 'styled-components';
import { setLiquorLiabilityCoverage, setCheckedEndorsement } from '../../../../actions/application';
import {
  selectAlcoholicBeverageSales,
  selectGLLimit,
  selectGrossAnnualSales,
  selectAkHash,
  selectCheckedEndorsements,
} from '../../../../selectors/application';
import { selectLoadingState } from '../../../../selectors';
import { liquorClasses100 } from '../../../../constants';
import { PAGES } from '../../../../constants';
import { LabelLarge } from '../../../../elements';
import EndorsementCard from '../../EndorsementCard';
import CenteredLoadingSpinner from '../../../AddOns/shared/CenteredLoadingSpinner';
import {
  SubQuestionCont,
  UnderwritingQuestionCont,
  UnderWritingNumberInput,
  StyledInputErrorMessage,
} from '../../styles/endorsements';
import { config, validateLiquorSalesInput } from './LiquorLiability.config';

const { endorsementKey: ENDORSEMENT_KEY, label: LABEL, description: DESCRIPTION } = config;

const LiquorLiability = () => {
  const dispatch = useDispatch();
  const alcoholicBeverageSales = useSelector(selectAlcoholicBeverageSales);
  const glLimit = useSelector(selectGLLimit);
  const grossAnnualSales = useSelector(selectGrossAnnualSales) || Infinity;
  const akHash = useSelector(selectAkHash);
  const isQuoteLoading = useSelector(selectLoadingState);

  const isDisabledByGlLimit = glLimit === 2_000_000;
  const isLiquorException = liquorClasses100.includes(akHash || '');
  const isSelected = useSelector(selectCheckedEndorsements).includes(ENDORSEMENT_KEY);
  const [isValid, setIsValid] = useState(true);
  const [localAlcoholicBeverageSales, setLocalAlcoholicBeverageSales] = useState<number | null | undefined>(
    alcoholicBeverageSales
  );

  useEffect(() => {
    if (localAlcoholicBeverageSales != null) {
      setIsValid(validateLiquorSalesInput(localAlcoholicBeverageSales, grossAnnualSales));
    }
  }, [localAlcoholicBeverageSales, grossAnnualSales]);

  const handleCheckboxChange = (checked: boolean) => {
    dispatch(setCheckedEndorsement(ENDORSEMENT_KEY, checked));
    if (checked && isLiquorException) {
      dispatch(setLiquorLiabilityCoverage({ alcoholicBeverageSales: grossAnnualSales }));
    } else {
      dispatch(setLiquorLiabilityCoverage(null));
      setLocalAlcoholicBeverageSales(null);
    }
  };

  const handleValueChange = (values: NumberFormatValues) => {
    const newValue = values.floatValue ?? null;
    setLocalAlcoholicBeverageSales(newValue);
    if (newValue != null) {
      setIsValid(validateLiquorSalesInput(newValue, grossAnnualSales));
    } else {
      setIsValid(true);
    }
  };

  const handleBlur = () => {
    if (localAlcoholicBeverageSales != null && isValid) {
      dispatch(setLiquorLiabilityCoverage({ alcoholicBeverageSales: localAlcoholicBeverageSales }));
    }
  };

  const description = isDisabledByGlLimit ? (
    <>
      This coverage is not available with a $2M liability limit. To add Liquor Liability Coverage, please{' '}
      <StyledLink
        to={{ type: PAGES.GENERAL_LIABILITY }}
        id={PAGES.GENERAL_LIABILITY}
        className="availableLink"
        data-cy={`sidebar-to-${PAGES.GENERAL_LIABILITY}`}
      >
        go back
      </StyledLink>{' '}
      and change your limits.
    </>
  ) : (
    DESCRIPTION
  );

  return (
    <EndorsementCard
      endorsementKey={ENDORSEMENT_KEY}
      label={LABEL}
      description={description}
      isSelected={isSelected}
      onCheckboxChange={handleCheckboxChange}
      disabled={isDisabledByGlLimit}
      hasExpandableContent={!isLiquorException}
    >
      {!isLiquorException && (
        <SubQuestionsContainer>
          {isQuoteLoading && <CenteredLoadingSpinner useAltColor />}
          <SubQuestionCont isActive={!isQuoteLoading} direction="column">
            <UnderwritingQuestionCont>
              <StyledLabelLarge htmlFor="alcoholicBeverageSales">What are your annual liquor sales?</StyledLabelLarge>
              <UnderWritingNumberInput
                name="alcoholicBeverageSales"
                value={localAlcoholicBeverageSales}
                onValueChange={handleValueChange}
                onBlur={handleBlur}
                customwidth="quarter"
                marginbottom="none"
                thousandSeparator={true}
                prefix="$"
                aria-label="What are your annual liquor sales?"
                data-cy="alcoholicBeverageSales"
                disabled={isQuoteLoading}
                hasError={!isValid}
                placeholder="e.g., $150,000"
              />
            </UnderwritingQuestionCont>
            {!isValid && (
              <StyledInputErrorMessage>
                Annual liquor sales cannot exceed half of gross annual sales
              </StyledInputErrorMessage>
            )}
          </SubQuestionCont>
        </SubQuestionsContainer>
      )}
    </EndorsementCard>
  );
};

const SubQuestionsContainer = styled.div`
  position: relative;
`;

const StyledLabelLarge = styled(LabelLarge)`
  align-self: center;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.neutral.medium} !important;
  text-decoration-skip-ink: none;
`;

export default LiquorLiability;
